/**
 * ContentRepository module
 *
 * This module create shortcut with a request
 * to the API
 *
 */

'use strict';

const jQuery = require('jquery');
const _ = require('lodash');

const RequestHandler = require('./../../../core/requestHandler');
const Request = require('./../../../core/request');
const Container = require('./../../../core/container');

const baseType = 'classcontent';
const HEADER_REMAINING_PAGE_TO_PUBLISH = 'X-Remaining-Page-To-Publish-Count';
const HEADER_PUBLISHED_COUNT = 'X-Published-Count';

let categories = null;

/**
 * Delete a content by Type and Uid
 *
 * @param  {string} type
 * @param  {string} uid
 * @returns {Promise} promise
 */
module.exports.delete = function(type, uid) {
    let currentApiUrl = RequestHandler.getBaseUrl();
    let dfd;

    RequestHandler.setBaseUrl('/api');
    dfd = RequestHandler.del('contents/' + type, {'uid': uid, 'page_uid': Container.get('page.uid')});
    RequestHandler.setBaseUrl(currentApiUrl);

    return dfd;
};

/**
 * Retrieve a content by Type and Uid
 *
 * @param  {string} type
 * @param  {string} uid
 * @returns {Promise} promise
 */
module.exports.find = function(type, uid) {
    if (!_.isString(type) || !_.isString(uid)) {
        throw new Error('Type or uid missing');
    }

    return RequestHandler.read(baseType + '/' + type, {'uid': uid});
};

/**
 * Retrieve all definitions
 *
 * @returns {Promise} promise
 */
module.exports.findDefinitions = function() {
    return RequestHandler.read(baseType, {'definition': ''});
};

/**
 * Find contents by uids
 *
 * @param  {Array} uids
 * @param  {string} siteUid
 * @returns {Promise} promise
 */
module.exports.findByUids = function(uids, siteUid) {
    if (!Array.isArray(uids)) {
        throw new Error('Uids must be an array');
    }

    let dfd = jQuery.Deferred();
    let ContentManager = require('./../component/ContentManager');

    siteUid = siteUid || '';

    uids.join();

    RequestHandler.read(baseType, {'uids': uids, 'site_uid': siteUid, 'preserve_order': true}).done(function(contents) {
        _.forEach(contents, function(content, key) {
            contents[key] = ContentManager.buildElement({
                'uid': content.uid,
                'type': content.type,
                'data': content,
            });
        });

        dfd.resolve(contents);
    }).fail(function(response) {
        dfd.reject(response);
    });

    return dfd.promise();
};

/**
 * Find all categories
 *
 * @returns {Promise} promise
 */
module.exports.findCategories = function() {
    let dfd = jQuery.Deferred();

    if (null === categories) {
        RequestHandler.read(baseType + '-category', {'page_uid': Container.get('page.uid')}).done(function(cats) {
            categories = cats;

            _.forEach(categories, function(value, keySection) {
                _.forEach(value.contents, function(value, keyContent) {
                    let type = value.type;
                    let match = type.match(/.*[\/](.*)/);

                    if (_.isArray(match)) {
                        type = match[1];
                    }

                    categories[keySection].contents[keyContent].icon = 'icon-' + type.toLowerCase();
                });
            });

            dfd.resolve(categories);
        }).fail(dfd.reject);
    } else {
        dfd.resolve(categories);
    }

    return dfd.promise();
};

/**
 * Find data of content
 *
 * @param {string} type
 * @param {string} uid
 * @returns {Promise} promise
 */
module.exports.findData = function(type, uid) {
    if (!_.isString(type) || !_.isString(uid)) {
        throw new Error('Type or uid missing');
    }

    return RequestHandler.read(baseType + '/' + type, {'uid': uid, 'concise': ''});
};

/**
 * Find info of content
 *
 * @param {string} type
 * @param {string} uid
 * @returns {Promise} promise
 */
module.exports.findInfo = function(type, uid) {
    if (!_.isString(type) || !_.isString(uid)) {
        throw new Error('Type or uid missing');
    }

    return RequestHandler.read(baseType + '/' + type, {'uid': uid, 'info': ''});
};

/**
 * Retrieve HTML of content with a optional rendermode
 *
 * @param {string} type
 * @param {string} uid
 * @param {string | null} rendermode
 * @returns {Promise} promise
 */
module.exports.getHTML = function(type, uid, rendermode) {
    if (!_.isString(type) || !_.isString(uid)) {
        throw new Error('Type or uid missing');
    }

    let request = new Request();
    let currentPageUid = Container.get('page.uid');
    let url = RequestHandler.getBaseUrl() + '/' + baseType + '/' + type + '/' + uid;

    request.addHeader('Accept', 'text/html');

    if (rendermode) {
        url = RequestHandler.addSearch(url, 'mode', rendermode);
    }

    if (currentPageUid) {
        url = RequestHandler.addSearch(url, 'page_uid', currentPageUid);
    }

    request.setUrl(url);

    return RequestHandler.send(request);
};

module.exports.getPagesToPublish = function() {
    let currentApiUrl = RequestHandler.getBaseUrl();
    let dfd = jQuery.Deferred();

    RequestHandler.setBaseUrl('/api');
    RequestHandler.read('contents/pages-to-commit').done(function(object, response) {
        dfd.resolve(object);
    }).fail(function() {
        dfd.reject();
    });
    RequestHandler.setBaseUrl(currentApiUrl);

    return dfd.promise();
};

module.exports.publish = function(pageUid) {
    let currentApiUrl = RequestHandler.getBaseUrl();
    let dfd = jQuery.Deferred();
    let currentPageUid = pageUid || Container.get('page.uid');

    RequestHandler.setBaseUrl('/api');
    RequestHandler.update('contents/commit-page/' + currentPageUid).done(function(object, response) {
        const publishedCount = response.getHeader(HEADER_PUBLISHED_COUNT) || response.getHeader(HEADER_PUBLISHED_COUNT.toLowerCase());
        const remainingPageToPublish = response.getHeader(HEADER_REMAINING_PAGE_TO_PUBLISH) || response.getHeader(HEADER_REMAINING_PAGE_TO_PUBLISH.toLowerCase());

        dfd.resolve(publishedCount, remainingPageToPublish);
    }).fail(function() {
        dfd.reject();
    });
    RequestHandler.setBaseUrl(currentApiUrl);

    return dfd.promise();
};

module.exports.cancel = function() {
    let currentApiUrl = RequestHandler.getBaseUrl();
    let currentPageUid = Container.get('page.uid');
    let result;

    RequestHandler.setBaseUrl('/api');
    result = RequestHandler.update('contents/reset-page/' + currentPageUid);
    RequestHandler.setBaseUrl(currentApiUrl);

    return result;
};

/**
 * Post or update content using the uid
 *
 * @param  {object} data
 * @returns {Promise} promise
 */
module.exports.save = function(data) {
    if (!data || !_.isString(data.type)) {
        throw new Error('Type must be set in ContentRepository::save');
    }

    let result;
    let pageUid = Container.get('page.uid');

    if (_.isString(data.uid)) {
        result = RequestHandler.update(baseType + '/' + data.type, data, {'uid': data.uid, 'page_uid': pageUid});
    } else {
        result = RequestHandler.create(baseType + '/' + data.type, data.data, {'page_uid': pageUid});
    }

    return result;
};

module.exports.upload = function(data) {
    return RequestHandler.create('/resource/upload', data);
};
